import { useEffect, useState } from "react";

/**
 * Mobile "go full-screen" affordance.
 *
 * A site cannot remove the browser address bar on its own — browsers require
 * either (a) the user installing the PWA / adding it to the home screen, which
 * our manifest + apple-mobile-web-app tags make launch chrome-less, or (b) a
 * user-gestured Fullscreen API call (Android Chrome only; iOS Safari has no
 * element fullscreen). This slim, dismissible bar surfaces whichever path the
 * current browser actually supports:
 *   - Android/Chromium with a captured `beforeinstallprompt` → "Install".
 *   - Android/Chromium otherwise → "Full screen" (Fullscreen API).
 *   - iOS Safari → text: Share → Add to Home Screen (the only path Apple allows).
 *
 * Self-gates to mobile viewports, hides once already standalone, persists
 * dismissal, and never renders under automation (keeps smoke screenshots clean).
 */

// Capture the install prompt at import time — it can fire before React mounts.
let deferredPrompt: (Event & { prompt: () => void; userChoice: Promise<unknown> }) | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as typeof deferredPrompt;
    window.dispatchEvent(new Event("desk:bip"));
  });
}

const DISMISS_KEY = "desk.pwa.hintDismissed";

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    window.matchMedia?.("(display-mode: fullscreen)").matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function InstallHint() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "other">("other");
  const [canInstall, setCanInstall] = useState(false);
  const [fsOn, setFsOn] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Keep out of Playwright smoke/e2e captures.
    if ((navigator as unknown as { webdriver?: boolean }).webdriver) return;
    if (isStandalone()) return;
    if (!window.matchMedia?.("(max-width: 1023.98px)").matches) return;
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      /* storage blocked — still show */
    }

    const ua = navigator.userAgent || "";
    const ios =
      /iphone|ipad|ipod/i.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setPlatform(ios ? "ios" : "other");
    setCanInstall(deferredPrompt !== null);
    setVisible(true);

    const onBip = () => setCanInstall(true);
    const onFs = () => setFsOn(document.fullscreenElement !== null);
    window.addEventListener("desk:bip", onBip);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("desk:bip", onBip);
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* user dismissed */
    }
    deferredPrompt = null;
    setCanInstall(false);
    dismiss();
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === null) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      /* not permitted */
    }
  };

  const fsSupported =
    typeof document !== "undefined" &&
    (document as unknown as { fullscreenEnabled?: boolean }).fullscreenEnabled === true;

  return (
    <div className="pwa-hint" role="dialog" aria-label="Play DESK full screen" data-testid="pwa-hint">
      <span className="pwa-hint-icon" aria-hidden>
        <img src="/assets/icons/icon-192.png" alt="" width={30} height={30} />
      </span>
      <div className="pwa-hint-body">
        {platform === "ios" ? (
          <p className="pwa-hint-text">
            Full screen: tap <span className="pwa-hint-kbd">Share</span> →{" "}
            <strong>Add to Home Screen</strong>
          </p>
        ) : (
          <p className="pwa-hint-text">Play full screen: hide the address bar</p>
        )}
      </div>
      <div className="pwa-hint-actions">
        {canInstall ? (
          <button
            type="button"
            className="pwa-hint-btn primary"
            onClick={install}
            data-testid="pwa-install"
          >
            Install
          </button>
        ) : fsSupported && platform !== "ios" ? (
          <button
            type="button"
            className="pwa-hint-btn primary"
            onClick={toggleFullscreen}
            data-testid="pwa-fullscreen"
          >
            {fsOn ? "Exit" : "Full screen"}
          </button>
        ) : null}
        <button
          type="button"
          className="pwa-hint-x"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="pwa-dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
